// Aktualitaets-Ping (Spec 2026-09-15 §5): eine offene Bearbeiten-Ansicht fragt
// alle 5 s nur den Stempel des Datensatzes ab (Admin: `updated_at`, Self-Service:
// Fingerabdruck `version`, Spec §6). Ist er neuer bzw. anders als der eigene,
// meldet der Hook das als `newer` — die Huelle zeigt daraufhin ein Banner mit
// „Übernehmen". Der Hook selbst laedt NICHTS nach und aendert kein Formular;
// das Zusammenfuehren macht `adoptFresh()` im jeweiligen Editor-Hook.
//
// Bewusst in `src/lib/`: die Self-Service-Flaeche liegt ausserhalb von Auth, hat
// ihr eigenes Bundle und darf kein Admin-Modul importieren — geteilt wird nur
// reine Logik (wie `threeWayMerge`, `konfliktUhrzeit`).
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Takt des Aktualitaets-Pings. 5 s seit 2026-09-15 (vorher 10 s): eine Anfrage
 * liest zwei Spalten eines Datensatzes per Primaerschluessel und laeuft nur in
 * offenen, sichtbaren Bearbeiten-Ansichten. Messung und Beobachtungshinweis:
 * `docs/systemstand.md`, Abschnitt „Aktualitäts-Ping". Tests spulen ueber diese
 * Konstante vor, damit Takt und Tests nicht auseinanderlaufen.
 */
export const FRESHNESS_POLL_MS = 5_000;

/**
 * Wie der fremde Stempel mit dem eigenen verglichen wird.
 * - `'newer'` (Standard, Admin): Zeitstempel, lexikografisch groesser = neuer.
 * - `'different'` (Self-Service): Fingerabdruecke sind nicht geordnet — jeder
 *   nicht-leere, ungleiche Wert zaehlt als neuer Stand.
 */
export type FreshnessCompare = 'newer' | 'different';

export interface FreshnessPollOptions<S> {
  /** Laeuft der Ping ueberhaupt? (Admin: Bearbeiten-Modus bzw. Auswahl im Panel.) */
  enabled: boolean;
  /** Abstand zwischen zwei Anfragen. */
  intervalMs?: number;
  /** Holt den Stempel vom Server. Fehler duerfen geworfen werden — sie bleiben still. */
  fetchStamp: () => Promise<S>;
  /** Vergleichsschluessel aus dem Stempel (Admin `s.updated_at`, Self `s.version`). */
  stampKey: (stamp: S) => string | null | undefined;
  /** Eigener Stand (derselbe Schluessel); alles Neuere bzw. Andere loest das Banner aus. */
  currentStamp: string | null;
  /** Vergleichsart, siehe `FreshnessCompare`. */
  compare?: FreshnessCompare;
  /** Voruebergehend aussetzen (waehrend eines Speichervorgangs). */
  paused?: boolean;
  /**
   * WORAUF sich der Ping bezieht (Sprecher-Id bzw. Self-Service-Token). Wechselt
   * der Wert, faellt ein stehendes Banner weg und der Zeitplan beginnt von vorn.
   * Ohne das bliebe im Split-View das Banner zu Person A ueber dem Formular von
   * Person B stehen — und weil ein stehendes Banner den Ping anhaelt, wuerde fuer
   * B nie wieder gefragt (das Panel wird ohne `key` gerendert, der Hook ueberlebt
   * den Wechsel also).
   */
  identity?: string | number | null;
}

export interface FreshnessPoll<S> {
  /** Fremder, neuerer Stand — sonst null. */
  newer: S | null;
  /**
   * Banner wegnehmen und wieder pingen. Der Normalfall braucht das nicht (ein
   * aufgeholter `currentStamp` raeumt es von selbst weg) — es ist der Ausweg
   * fuer Huellen, die das Banner aus eigenem Anlass schliessen wollen.
   */
  dismiss: () => void;
}

/**
 * Ist der fremde Stempel echt neuer als der eigene? Zeitstempel kommen als
 * „YYYY-MM-DD HH:MM:SS" (bzw. ISO) — beides ist lexikografisch sortierbar, ein
 * String-Vergleich genuegt also ohne Date-Parsing.
 *
 * Wichtig ist das „>": mit reiner Ungleichheit bliebe ein Banner stehen, sobald
 * der eigene Stand den fremden UEBERHOLT — genau das passiert, wenn man auf ein
 * Banner hin nicht „Übernehmen", sondern „Speichern" klickt (409 → Abgleich →
 * erneutes Speichern mit noch neuerem Stempel). Das Banner haenge dann fuer den
 * Rest der Sitzung, und mit ihm der Ping.
 */
function istNeuer(fremd: string | null | undefined, eigen: string | null): boolean {
  if (!fremd) return false;
  if (!eigen) return true; // eigener Stand unbekannt → im Zweifel melden
  return fremd > eigen;
}

/**
 * Meldet der Server einen Zwischenstand (`settling: true`, Spec 2026-09-15 §7)?
 * Waehrend `renameVoiceAudios` Datei fuer Datei verschiebt, aendert sich der
 * Self-Fingerabdruck pro Datei, und ein Übernehmen holte einen halb umbenannten
 * Stand. Ein solcher Stempel meldet nichts und raeumt nichts weg — der naechste
 * Tick entscheidet.
 */
function istZwischenstand(stamp: unknown): boolean {
  return typeof stamp === 'object' && stamp !== null && 'settling' in stamp && stamp.settling === true;
}

/**
 * Meldet der fremde Schluessel einen anderen Stand? Bei `'different'` reicht
 * Ungleichheit. Das gilt nur fuers MELDEN — wann ein stehendes Banner wieder
 * wegfaellt, regelt `bannerGilt` (dort hat `'different'` sein eigenes
 * Ueberholen-Problem).
 */
function meldet(fremd: string | null | undefined, eigen: string | null, compare: FreshnessCompare): boolean {
  if (compare === 'different') return !!fremd && fremd !== eigen;
  return istNeuer(fremd, eigen);
}

export function useFreshnessPoll<S>(
  {
    enabled, intervalMs = FRESHNESS_POLL_MS, fetchStamp, stampKey, currentStamp,
    compare = 'newer', paused = false, identity = null,
  }: FreshnessPollOptions<S>,
): FreshnessPoll<S> {
  // Das Banner merkt sich neben dem fremden Stempel den EIGENEN Stand zum
  // Zeitpunkt der Meldung (`seite`) — siehe `bannerGilt`.
  const [meldung, setMeldung] = useState<{ stamp: S; seite: string | null } | null>(null);
  const [bezug, setBezug] = useState<string | number | null>(identity);
  const newer = meldung?.stamp ?? null;

  /**
   * Gilt ein stehendes Banner noch?
   * - `'newer'`: solange der fremde Zeitstempel echt neuer ist als der eigene
   *   (holt der eigene auf oder ueberholt er, faellt es weg).
   * - `'different'`: nur solange sich der eigene Stand seit der Meldung NICHT
   *   veraendert hat. Fingerabdruecke sind ungeordnet; auf „eigen == fremd" zu
   *   warten reicht nicht: lief der Server vor dem „Übernehmen" weiter (etwa
   *   Zwischenstaende beim Umbenennen mehrerer Proben, zwei Admin-Speichern in
   *   Folge), steht die Seite danach auf Z, das Banner auf X — es bliebe bis zum
   *   Reload stehen, und mit ihm der Ping. Faellt es stattdessen bei jeder
   *   Aenderung des eigenen Stands weg (Übernehmen, eigenes Speichern), laeuft
   *   der Ping wieder und meldet neu, falls der Server noch abweicht.
   */
  function bannerGilt(m: { stamp: S; seite: string | null }): boolean {
    if (compare === 'different') return currentStamp === m.seite;
    return meldet(stampKey(m.stamp), currentStamp, compare);
  }

  // Zustands-Anpassung waehrend des Renderns (React: „adjusting state when props
  // change") statt im Effekt: so ist das Banner im SELBEN Render weg, in dem der
  // Wechsel bzw. der neue eigene Stand ankommt — kein Zwischenbild mit einem
  // Banner, das nicht mehr gilt.
  if (identity !== bezug) {
    setBezug(identity);
    if (meldung !== null) setMeldung(null);
  } else if (meldung !== null && !bannerGilt(meldung)) {
    setMeldung(null);
  }

  // `fetchStamp` und `currentStamp` liegen in Refs, damit das Intervall nicht bei
  // jedem Render neu startet: die Huellen geben `fetchStamp` inline herein (neue
  // Identitaet pro Render), und `currentStamp` aendert sich nach jedem Speichern.
  // In der Dependency-Liste wuerde der Timer dabei jedes Mal zurueckgesetzt — bei
  // haeufigen Renders kaeme nie ein Tick zustande.
  // `stampKey`/`compare` aus demselben Grund (inline gereichte Funktion).
  const fetchRef = useRef(fetchStamp);
  const keyRef = useRef(stampKey);
  const compareRef = useRef(compare);
  const currentRef = useRef(currentStamp);
  useEffect(() => { fetchRef.current = fetchStamp; });
  useEffect(() => { keyRef.current = stampKey; });
  useEffect(() => { compareRef.current = compare; });
  useEffect(() => { currentRef.current = currentStamp; });

  // Solange ein Banner steht, wird nicht weiter gefragt: die Antwort waere
  // dieselbe, und das Banner zeigt ohnehin schon den fremden Stand.
  const aktiv = enabled && !paused && newer === null;

  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    let laeuft = false;
    async function tick() {
      // Versteckter Tab: nicht fragen. Ein Hintergrund-Tab braucht kein Banner,
      // und beim Zurueckkommen kommt der naechste Tick von allein.
      if (typeof document !== 'undefined' && document.hidden) return;
      if (laeuft) return; // langsame Antwort: kein zweiter Ping obendrauf
      laeuft = true;
      try {
        const stamp = await fetchRef.current();
        if (abgebrochen) return;
        if (istZwischenstand(stamp)) return; // Umbenennen laeuft — einfach weiter takten
        const seite = currentRef.current;
        if (meldet(keyRef.current(stamp), seite, compareRef.current)) setMeldung({ stamp, seite });
      } catch {
        // Still (Spec §5): ein gescheiterter Ping ist kein Nutzerproblem —
        // kein Toast, kein Abbruch. Der naechste Versuch folgt nach dem Intervall.
      } finally {
        laeuft = false;
      }
    }
    // `identity` in den Dependencies: nach einem Wechsel beginnt der Zeitplan von
    // vorn, statt den halb abgelaufenen Takt des vorigen Datensatzes zu erben.
    const id = setInterval(() => { void tick(); }, intervalMs);
    return () => { abgebrochen = true; clearInterval(id); };
  }, [aktiv, intervalMs, identity]);

  const dismiss = useCallback(() => setMeldung(null), []);
  return { newer, dismiss };
}
